import { useCallback, useEffect, useRef, useState } from 'react';
import type { Snapshot } from '@platform/contracts/x';
import {usePlayerSession} from './session';
import {useCopy,useUi} from '@platform/ui/config';
import {createClientId} from './clientId';
import {purchaseInProgress,purchaseSummary,type PurchaseResult} from './purchaseSummary';
export type PurchaseIntent = {path:string;drawNumber?:string|null;body:{requestId:string;drawId:string;amountMinor:number;cardIds?:string[];marketId?:string}};
const storageKey=(game:string)=>'tombalax120.pending.'+game;
function restore(scope:string,game:string):PurchaseIntent|null{
  try {
    const raw=sessionStorage.getItem(storageKey(scope));if(!raw)return null;const value=JSON.parse(raw) as PurchaseIntent;
    return typeof value?.body?.requestId==='string'&&typeof value.body.drawId==='string'&&value.path.startsWith(game+'/entries')?value:null;
  }catch{return null;}
}
// ADR-001/002: unknown responses retain the original command ID. A retry can never create a second debit.
export function usePurchase(game:string, onSnapshot:(snapshot:Snapshot)=>void) {
  const session=usePlayerSession(),t=useCopy();
  const receiptRefreshMilliseconds=useUi().controls.purchaseReceiptRefreshMilliseconds;
  const scope=session?game+'.'+session.id:game;
  const [pending,setPending]=useState<PurchaseIntent|null>(()=>restore(scope,game));
  const [busy,setBusy]=useState(false);const [outcome,setOutcome]=useState('');
  const [result,setResult]=useState<PurchaseResult|null>(null);
  const [outcomeDrawId,setOutcomeDrawId]=useState('');
  const [outcomeDrawNumber,setOutcomeDrawNumber]=useState<string|null>(null);
  const clearOutcome=useCallback(()=>{setOutcome('');setResult(null);setOutcomeDrawId('');setOutcomeDrawNumber(null);},[]);
  const inFlight=useRef<string|null>(null);const pendingRef=useRef(pending);
  const isPending=(intent:PurchaseIntent)=>pendingRef.current?.body.requestId===intent.body.requestId;
  const finish=(intent:PurchaseIntent)=>{
    if(inFlight.current===intent.body.requestId){inFlight.current=null;setBusy(false);}
  };
  const refresh=useRef<AbortController|null>(null);
  useEffect(()=>()=>refresh.current?.abort(),[]);
  const remember=(intent:PurchaseIntent|null)=>{pendingRef.current=intent;setPending(intent);try{if(intent)sessionStorage.setItem(storageKey(scope),JSON.stringify(intent));else sessionStorage.removeItem(storageKey(scope));}catch{/* Memory receipt still protects retries in this page. */}};
  const applyReceipt=async(intent:PurchaseIntent,receipt:{status:string}|PurchaseResult)=>{
    if(!isPending(intent))return false;
    setOutcomeDrawId(intent.body.drawId);setOutcomeDrawNumber(intent.drawNumber??null);
    if('items' in receipt){setResult(receipt);setOutcome(receipt.pending>0?'processing':receipt.status==='Partial'?'partial':receipt.status==='Accepted'?'accepted':'notAccepted');}
    else if(!['Accepted','Partial','NotAccepted'].includes(receipt.status)){setResult(null);setOutcome('uncertain');}
    if(['Accepted','Partial','NotAccepted'].includes(receipt.status)){
      remember(null);finish(intent);setOutcome(receipt.status==='Accepted'?'accepted':receipt.status==='Partial'?'partial':'notAccepted');
      // The terminal receipt settles the command. A slow/failed readback must
      // neither keep controls locked nor turn an accepted purchase into unknown.
      // Snapshot revision guards still reject a readback older than the stream.
      refresh.current?.abort();const controller=new AbortController();refresh.current=controller;
      void fetch('/api/state',{signal:controller.signal}).then(async state=>{
        if(state.ok){const snapshot=await state.json() as Snapshot;if(!controller.signal.aborted)onSnapshot(snapshot);}
      }).catch(()=>{/* The live stream/reconnect supplies the next authoritative snapshot. */});
      return true;
    }
    return false;
  };
  const resolve=async(intent:PurchaseIntent)=>{
    const response=await fetch('/api/player/requests/'+encodeURIComponent(intent.body.drawId)+'/'+encodeURIComponent(intent.body.requestId));
    if(!response.ok){if(isPending(intent))setOutcome('uncertain');return false;}
    return applyReceipt(intent,await response.json() as {status:string}|PurchaseResult);
  };
  const send=async(intent:PurchaseIntent)=>{
    if(inFlight.current)return;
    inFlight.current=intent.body.requestId;setBusy(true);setOutcome('');setResult(null);setOutcomeDrawId(intent.body.drawId);setOutcomeDrawNumber(intent.drawNumber??null);
    remember(intent);
    try {
      const response=await fetch('/api/'+intent.path,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(intent.body)});
      // Receipt polling may already have settled this request while its POST
      // was in transit. A late response must not overwrite that final result.
      if(!isPending(intent))return;
      if(response.status===202){await applyReceipt(intent,await response.json() as PurchaseResult);return;}
      if(response.ok){
        const snapshot=await response.json() as Snapshot;if(!isPending(intent))return;
        onSnapshot(snapshot);remember(null);setOutcome('accepted');return;
      }
      const error=await response.json().catch(()=>({})) as {code?:string};
      if(!isPending(intent))return;
      if(error.code==='wrong_draw'||error.code==='request_conflict'){if(!await resolve(intent)&&isPending(intent))setOutcome('uncertain');return;}
      if(response.status===409&&error.code){remember(null);setOutcome(error.code);return;}
      setOutcome('uncertain');
    }catch{if(isPending(intent))setOutcome('uncertain');}
    // A settled receipt can unlock a new command before this older POST ends.
    // Its completion may release only its own lock, never the newer command's.
    finally{finish(intent);}
  };
  useEffect(()=>{
    const intent=pendingRef.current;if(!intent)return;let active=true,inProgress=false;let timer:ReturnType<typeof setTimeout>|undefined;
    const schedule=()=>{clearTimeout(timer);if(active&&isPending(intent)&&!document.hidden)timer=setTimeout(()=>void poll(),receiptRefreshMilliseconds);};
    const poll=async()=>{
      clearTimeout(timer);if(!active||!isPending(intent)||inProgress||document.hidden)return;inProgress=true;
      try{await resolve(intent);}catch{if(active&&isPending(intent))setOutcome('uncertain');}finally{inProgress=false;schedule();}
    };
    const receipt=(event:Event)=>{
      const data=(event as CustomEvent<PurchaseResult & {drawId:string;requestId:string}>).detail;
      if(data?.drawId===intent.body.drawId&&data.requestId===intent.body.requestId)void applyReceipt(intent,data);
    };
    const visible=()=>{if(document.hidden)clearTimeout(timer);else void poll();};
    window.addEventListener('tombala:purchase-receipt',receipt);document.addEventListener('visibilitychange',visible);
    // A new POST normally finishes through the existing socket. Reload/recovery
    // reads the original receipt immediately; the timer is only a fallback.
    if(inFlight.current===intent.body.requestId)schedule();else void poll();
    return()=>{active=false;clearTimeout(timer);window.removeEventListener('tombala:purchase-receipt',receipt);document.removeEventListener('visibilitychange',visible);};
  },[pending?.body.requestId,receiptRefreshMilliseconds]);
  const submit=(path:string,body:Omit<PurchaseIntent['body'],'requestId'>,drawNumber?:string|null)=>{
    let intent=pendingRef.current;
    if(!intent){
      try{intent={path,drawNumber,body:{...body,requestId:createClientId()}};}
      catch{setOutcome('notStarted');return Promise.resolve();}
    }
    return send(intent);
  };
  const summary=purchaseSummary(result,t);
  const processing=!!pending&&outcome==='processing'&&purchaseInProgress(result);
  return {pending,busy,processing,outcome,outcomeDrawId,outcomeDrawNumber,result,summary,clearOutcome,submit,retry:()=>pendingRef.current?send(pendingRef.current):Promise.resolve()};
}
